import type { FC, ReactNode } from 'react'

import { useEffect, useState } from 'react'
import { ArrowRight, Menu, X } from 'lucide-react'
import { cn } from '@/lib/utils'
import { Logo } from '@/components/generals/logo'

import { navLinks } from '@/constants/landing'
import { siteConfig } from '@/constants/site'

export const Navbar: FC = (): ReactNode => {
    const [scrolled, setScrolled] = useState(false)
    const [open, setOpen] = useState(false)

    useEffect(() => {
        const onScroll = () => setScrolled(window.scrollY > 12)

        onScroll()
        window.addEventListener('scroll', onScroll, { passive: true })

        return () => window.removeEventListener('scroll', onScroll)
    }, [])

    useEffect(() => {
        if (!open) return

        const onKey = (event: KeyboardEvent) => {
            if (event.key === 'Escape') setOpen(false)
        }

        const onResize = () => {
            if (window.innerWidth >= 768) setOpen(false)
        }

        window.addEventListener('keydown', onKey)
        window.addEventListener('resize', onResize)

        return () => {
            window.removeEventListener('keydown', onKey)
            window.removeEventListener('resize', onResize)
        }
    }, [open])

    const elevated = scrolled || open

    return (
        <header className='fixed inset-x-0 top-0 z-50 px-3 pt-3 sm:px-4'>
            <div
                className={cn(
                    'mx-auto max-w-6xl rounded-full border transition-all duration-500',
                    elevated ? 'border-border/80 bg-white/80 elevate backdrop-blur-xl' : 'border-transparent bg-white/0'
                )}
            >
                <nav aria-label="Main" className='flex h-14 items-center justify-between pl-4 pr-2 sm:pl-5'>
                    <a href='#hero' className='flex items-center gap-2.5 rounded-full text-ink' aria-label={`${siteConfig.name} home`}>
                        <Logo className='size-7' />

                        <span className='text-[17px] font-semibold tracking-[-0.02em]'>
                            {siteConfig.name}
                        </span>
                    </a>

                    <ul className='hidden items-center gap-0.5 md:flex'>
                        {navLinks.map(link => (
                            <li key={link.href}>
                                <a
                                    href={link.href}
                                    className='rounded-full px-3.5 py-2 text-sm text-muted-foreground transition-colors hover:bg-secondary hover:text-foreground'
                                >
                                    {link.label}
                                </a>
                            </li>
                        ))}
                    </ul>

                    <div className='flex items-center gap-1.5'>
                        <a
                            href='#access'
                            className='group hidden h-10 items-center gap-1.5 rounded-full bg-ink pl-4 pr-3.5 text-sm font-medium text-white transition-colors hover:bg-ink/85 sm:inline-flex'
                        >
                            Get early access
                            <ArrowRight className='size-4 transition-transform group-hover:translate-x-0.5' />
                        </a>

                        <button
                            type='button'
                            className='inline-flex size-10 items-center justify-center rounded-full text-ink transition-colors hover:bg-secondary md:hidden'
                            aria-expanded={open}
                            aria-controls='mobile-menu'
                            aria-label={open ? "Close menu" : "Open menu"}
                            onClick={() => setOpen(value => !value)}
                        >
                            {open ? <X className='size-5' /> : <Menu className='size-5' />}
                        </button>
                    </div>
                </nav>
            </div>

            {open && (
                <div
                    id='mobile-menu'
                    className='mx-auto mt-2 max-w-6xl rounded-3xl border border-border/80 bg-white/95 p-2 elevate-lg backdrop-blur-xl animate-in fade-in slide-in-from-top-2 duration-200 md:hidden'
                >
                    <ul className='flex flex-col'>
                        {navLinks.map(link => (
                            <li key={link.href}>
                                <a
                                    href={link.href}
                                    onClick={() => setOpen(false)}
                                    className='flex items-center justify-between rounded-2xl px-4 py-3 text-[15px] font-medium text-ink transition-colors hover:bg-secondary'
                                >
                                    {link.label}
                                    <ArrowRight className='size-4 text-muted-foreground' />
                                </a>
                            </li>
                        ))}
                    </ul>

                    <a
                        href='#access'
                        onClick={() => setOpen(false)}
                        className='mt-2 flex h-12 items-center justify-center gap-2 rounded-2xl bg-ink text-[15px] font-medium text-white'
                    >
                        Get early access
                        <ArrowRight className='size-4' />
                    </a>
                </div>
            )}
        </header>
    )
}
